import React, { useState } from 'react';
import './SyllabusUpload.css';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// Use local worker instead of CDN
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.js';

const SyllabusUpload = () => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState({
    subjects: [],
    assignments: [],
    tests: []
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  
  const [newSubject, setNewSubject] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [newAssignment, setNewAssignment] = useState('');
  const [newTest, setNewTest] = useState('');
  const [isUploaded, setIsUploaded] = useState(false);
  const [editingIndex, setEditingIndex] = useState({ type: null, index: null });
  const [editValue, setEditValue] = useState('');

  const parseSubjects = (text) => {
    const bulletPointPattern = /^[•\-\u2013\u2014]\s*(.+)$/gmi;
    const sectionHeaderPattern = /(Tentative Course Content|Course Topics|Topics|Course Content):?/gi;

    const subjects = [];
    let lines = text.split('\n');
    let inSubjectSection = false;

    for (let line of lines) {
      if (sectionHeaderPattern.test(line)) {
        console.log('Found subject section header:', line);
        inSubjectSection = true;
        continue;
      }
      if (inSubjectSection) {
        if (line.trim() === '' || /^[1-9]/.test(line)) break;
        let match;
        bulletPointPattern.lastIndex = 0;
        while ((match = bulletPointPattern.exec(line)) !== null) {
          console.log('Found subject line:', match[1]);
          subjects.push(match[1].trim());
        }
      }
    }

    return [...new Set(subjects)];
  };

  const parseAssignments = (text) => {
    const assignmentPattern = /(homework assignments?|labs?|projects?)\s*(are|will be|due|:)?\s*([^\n]*)/gi;
    const assignments = [];

    let match;
    while ((match = assignmentPattern.exec(text)) !== null) {
      const entry = match[0].trim();
      if (entry.length > 10) {
        console.log('Found assignment:', entry);
        assignments.push(entry);
      }
    }

    return [...new Set(assignments)];
  };

  const parseTests = (text) => {
    const testPattern = /(quiz|exam|midterm|final)\s*\d*\s*(on)?\s*[A-Za-z]+\s+\d{1,2}/gi;
    const tests = [];

    let match;
    while ((match = testPattern.exec(text)) !== null) {
      console.log('Found test:', match[0]);
      tests.push(match[0].trim());
    }

    return [...new Set(tests)];
  };

  const convertToText = async (file) => {
    const fileType = file.type;
    console.log('Converting file type:', fileType);

    try {
      // Plain text files
      if (fileType === 'text/plain') {
        return await file.text();
      }
      
      // Word documents
      if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
          fileType === 'application/msword') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value;
      }
      
      // PDF files
      if (fileType === 'application/pdf') {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
          
          // Add loading task error handling
          loadingTask.onProgress = (progress) => {
            console.log(`Loading PDF: ${progress.loaded}/${progress.total}`);
          };

          const pdf = await loadingTask.promise;
          console.log('PDF loaded successfully');
          
          let fullText = '';
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\n';
          }
          return fullText;
        } catch (pdfError) {
          console.error('PDF processing error:', pdfError);
          throw new Error('Failed to process PDF file. Please try a different file format.');
        }
      }

      throw new Error(`Unsupported file type: ${fileType}`);
    } catch (error) {
      console.error('Error converting file:', error);
      throw new Error(`Could not convert file: ${error.message}`);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setMessage({ text: 'File selected: ' + selectedFile.name, type: 'success' });
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage({ text: 'Please select a file first', type: 'error' });
      return;
    }
  
    try {
      setMessage({ text: 'Converting file...', type: 'info' });
      
      const text = await convertToText(file);
      console.log('File converted to text');
  
      const subjects = parseSubjects(text);
      const assignments = parseAssignments(text);
      const tests = parseTests(text);
  
      setParsedData({ subjects, assignments, tests });
  
      if (subjects.length > 0) {
        const syllabusData = {
          topics: subjects,
          lastSyncTime: new Date().toISOString()
        };
        localStorage.setItem('syllabusData', JSON.stringify(syllabusData));
      }
  
      if (subjects.length === 0 && assignments.length === 0 && tests.length === 0) {
        setMessage({ text: 'Parsed file but no subjects, assignments, or tests were found.', type: 'warning' });
      } else {
        setMessage({ text: 'File parsed successfully!', type: 'success' });
        setIsUploaded(true);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setMessage({ text: error.message || 'Error processing file', type: 'error' });
      setIsUploaded(false);
    }
  }; 

  const handleAddSubject = () => {
    if (newSubject.trim()) {
      setParsedData(prev => ({
        ...prev,
        subjects: [...prev.subjects, newSubject.trim()]
      }));
      setNewSubject('');
    }
  };

  const handleDeleteSubject = (index) => {
    setParsedData(prev => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index)
    }));
  };

  const handleAddAssignment = () => {
    if (newAssignment.trim()) {
      setParsedData(prev => ({
        ...prev,
        assignments: [...prev.assignments, newAssignment.trim()]
      }));
      setNewAssignment('');
    }
  };

  const handleDeleteAssignment = (index) => {
    setParsedData(prev => ({
      ...prev,
      assignments: prev.assignments.filter((_, i) => i !== index)
    }));
  };

  const handleAddTest = () => {
    if (newTest.trim()) {
      setParsedData(prev => ({
        ...prev,
        tests: [...prev.tests, newTest.trim()]
      }));
      setNewTest('');
    }
  };

  const handleDeleteTest = (index) => {
    setParsedData(prev => ({
      ...prev,
      tests: prev.tests.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = (type, index, currentValue) => {
    setEditingIndex({ type, index });
    setEditValue(currentValue);
  };

  const handleSaveEdit = (type) => {
    if (editValue.trim()) {
      setParsedData(prev => ({
        ...prev,
        [type]: prev[type].map((item, i) => 
          i === editingIndex.index ? editValue.trim() : item
        )
      }));
    }
    setEditingIndex({ type: null, index: null });
    setEditValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit(editingIndex.type);
    } else if (e.key === 'Escape') {
      setEditingIndex({ type: null, index: null });
      setEditValue('');
    }
  };

  return (
    <div className="syllabus-upload">
      <h2>Upload Syllabus</h2>
      
      {/* File upload section */}
      <div className="upload-section">
        <div className="file-input-container">
          <input
            type="file"
            onChange={handleFileChange}
            className="file-input"
            id="file-input"
            accept=".txt,.doc,.docx,.pdf"
          />
          <label htmlFor="file-input" className="file-label">
            {file ? file.name : 'Choose a file'}
          </label>
          <button 
            onClick={handleUpload}
            className="upload-button"
            disabled={!file}
          >
            Upload
          </button>
        </div>
        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}
      </div>

      {/* Only show sections after successful upload and parse */}
      {isUploaded && (
        <>
          {/* Subjects/Topics section */}
          <div className="content-section">
            <h3>Subjects/Topics Found:</h3>
            <div className="add-item-container">
              <input
                type="text"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                placeholder="Add new subject/topic"
                className="add-item-input"
              />
              <button onClick={handleAddSubject} className="add-button">Add</button>
            </div>
            <ul className="items-list">
              {parsedData.subjects.map((subject, index) => (
                <li key={index} className="item">
                  {editingIndex.type === 'subjects' && editingIndex.index === index ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleSaveEdit('subjects')}
                      onKeyDown={handleKeyPress}
                      className="edit-input"
                      autoFocus
                    />
                  ) : (
                    <span>{subject}</span>
                  )}
                  <div className="item-actions">
                    <button 
                      onClick={() => handleEdit('subjects', index, subject)} 
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteSubject(index)} 
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Assignments section */}
          <div className="content-section">
            <h3>Assignments Found:</h3>
            <div className="add-item-container">
              <input
                type="text"
                value={newAssignment}
                onChange={(e) => setNewAssignment(e.target.value)}
                placeholder="Add new assignment"
                className="add-item-input"
              />
              <button onClick={handleAddAssignment} className="add-button">Add</button>
            </div>
            <ul className="items-list">
              {parsedData.assignments.map((assignment, index) => (
                <li key={index} className="item">
                  {editingIndex.type === 'assignments' && editingIndex.index === index ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleSaveEdit('assignments')}
                      onKeyDown={handleKeyPress}
                      className="edit-input"
                      autoFocus
                    />
                  ) : (
                    <span>{assignment}</span>
                  )}
                  <div className="item-actions">
                    <button 
                      onClick={() => handleEdit('assignments', index, assignment)}
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteAssignment(index)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Tests section */}
          <div className="content-section">
            <h3>Tests/Exams Found:</h3>
            <div className="add-item-container">
              <input
                type="text"
                value={newTest}
                onChange={(e) => setNewTest(e.target.value)}
                placeholder="Add new test/exam"
                className="add-item-input"
              />
              <button onClick={handleAddTest} className="add-button">Add</button>
            </div>
            <ul className="items-list">
              {parsedData.tests.map((test, index) => (
                <li key={index} className="item">
                  {editingIndex.type === 'tests' && editingIndex.index === index ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => handleSaveEdit('tests')}
                      onKeyDown={handleKeyPress}
                      className="edit-input"
                      autoFocus
                    />
                  ) : (
                    <span>{test}</span>
                  )}
                  <div className="item-actions">
                    <button 
                      onClick={() => handleEdit('tests', index, test)}
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteTest(index)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default SyllabusUpload;
